import { useCallback } from 'react'
import { isPlanSettingsElement } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { isInPlanStorey, resolveSelectedElementType } from '@editor/utils'
import {
  useControlStore,
  useEditorViewStore,
  useModelStore,
  useSelectionStore,
} from '@editorStores'
import { useGuidToElementIncludingRips } from '@editorHooks'
import { getPlanViewStorey } from '../utils'

/**
 * Selects the element behind a geometry or label clicked in the plan view and opens the "Bauteil"
 * tab with its settings. Elements without plan settings, or not drawn on the plan storeys (the
 * ones the scene doesn't highlight on hover), are ignored.
 */
const useSelectPlanViewElement = (): ((guid: string) => void) => {
  const select = useSelectionStore(state => state.select)
  const setPlanViewTab = useEditorViewStore(state => state.setPlanViewTab)
  const guidToElement = useGuidToElementIncludingRips()
  const storey = useControlStore(state => state.floorplanState.storey)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  const planStorey = getPlanViewStorey(storey, visibleStoreys)

  return useCallback(
    (guid: string) => {
      const element = guidToElement[guid]
      if (!element || !isPlanSettingsElement(element)) return
      if (planStorey == null || !isInPlanStorey(element, planStorey)) return
      select(
        {
          type: 'planar',
          guids: [guid],
          elementType: resolveSelectedElementType(useModelStore.getState().model, guid),
        },
        // The plan view has no element modes: the selection only drives its "Bauteil" tab.
        { adopt: 'keep' },
      )
      setPlanViewTab('element')
    },
    [guidToElement, planStorey, select, setPlanViewTab],
  )
}

export default useSelectPlanViewElement
